import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import { createFilterOptions } from "@mui/material/Autocomplete";
import ModalBasicLayout from "./ModalBasicLayout";
import TextField from "../Form/TextField";
import AutocompleteField from "../Form/AutocompleteField";
import ContainerModalActions from "../Containers/ContainerModalActions";
import Loader from "../Loaders/Loader";
import { SimpleAlert } from "../Alerts/Alerts";
import useFocusErrorForm from "../../hooks/useFocusErrorForm";
import { useDispatchApp } from "../../lib/redux";
import { useTranslationApp } from "../../lib/i18next";
import {
  startGetBusinessCentersTikTokBusiness,
  startCreateAdvertiserTikTokBusiness,
} from "../../actions/user";
import { startGetBrandInformation } from "../../actions/adsCampaigns";
import { getNameBusinessTheme } from "../../actions/getters";
import {
  ALERT_ICON_TYPE_SUCCESS,
  COUNTRIES,
  tiktokCurrenciesData,
} from "../../utils/constants";
import { TIKTOK_BUSINESS_INDUSTRIES } from "../../utils/tiktokBusinessIndustries";
import {
  BUTTON_CONNECT_ACCOUNT,
  BUTTON_CREATE,
  BUTTON_GO_BACK,
  FIELD_MAX_LENGTH,
  FIELD_REQUIRED,
  MODAL_CREATE_AD_ACCOUNT_TIKTOK_BUSINESS_CENTER,
  MODAL_CREATE_AD_ACCOUNT_TIKTOK_COMPANY,
  MODAL_CREATE_AD_ACCOUNT_TIKTOK_COUNTRY,
  MODAL_CREATE_AD_ACCOUNT_TIKTOK_CURRENCY,
  MODAL_CREATE_AD_ACCOUNT_TIKTOK_CURRENCY_HELPER,
  MODAL_CREATE_AD_ACCOUNT_TIKTOK_DESCRIPTION,
  MODAL_CREATE_AD_ACCOUNT_TIKTOK_INDUSTRY,
  MODAL_CREATE_AD_ACCOUNT_TIKTOK_NAME,
  MODAL_CREATE_AD_ACCOUNT_TIKTOK_CREDIT_ALERT,
  MODAL_CREATE_AD_ACCOUNT_TIKTOK_REVIEW_NOTE,
  MODAL_CREATE_AD_ACCOUNT_TIKTOK_SUCCESS_TITLE,
  MODAL_CREATE_AD_ACCOUNT_TIKTOK_TIMEZONE,
  MODAL_CREATE_AD_ACCOUNT_TIKTOK_TITLE,
  OK,
} from "../../i18n/keysTranslations";

const CONNECT_ACCOUNT_OPTION_VALUE = "__connect_account__";
const BUSINESS_CENTER_VALUE_SEPARATOR = "|";
const DEFAULT_CURRENCY = "USD";
const MAX_NAME_LENGTH = 100;
const MAX_COMPANY_LENGTH = 255;

const filter = createFilterOptions();

const getBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
};

const getTimezoneOptions = () => {
  const browserTimezone = getBrowserTimezone();
  let timezones = [];

  if (typeof Intl.supportedValuesOf === "function") {
    timezones = Intl.supportedValuesOf("timeZone");
  }

  if (browserTimezone && !timezones.includes(browserTimezone)) {
    timezones = [browserTimezone, ...timezones];
  }

  return timezones.map((timezone) => ({ value: timezone, label: timezone }));
};

const buildBusinessCenterValue = ({ integrationID, bcID }) =>
  `${integrationID}${BUSINESS_CENTER_VALUE_SEPARATOR}${bcID}`;

const parseBusinessCenterValue = (value = "") => {
  const [integrationID, ...bcParts] = value.split(
    BUSINESS_CENTER_VALUE_SEPARATOR
  );
  return { integrationID, bcID: bcParts.join(BUSINESS_CENTER_VALUE_SEPARATOR) };
};

const ModalCreateAdAccountTikTokBusiness = ({
  modalOpen,
  integrationIDs = [],
  onCloseModal = () => {},
  onCallbackCreate = () => {},
  onConnectAccount = () => {},
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [businessCenters, setBusinessCenters] = useState([]);
  const [lockedCurrency, setLockedCurrency] = useState("");
  const loadGenerationRef = useRef(0);
  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();
  const NAME_BUSINESS = dispatch(getNameBusinessTheme());

  const { control, setValue, formState, handleSubmit } = useForm({
    defaultValues: {
      businessCenter: "",
      name: "",
      company: "",
      industry: "",
      country: "",
      currency: DEFAULT_CURRENCY,
      timezone: getBrowserTimezone(),
    },
  });
  useFocusErrorForm(formState);
  const { errors } = formState;

  const requiredRule = {
    required: { value: true, message: t(FIELD_REQUIRED) },
  };

  const industryOptions = TIKTOK_BUSINESS_INDUSTRIES;
  const countryOptions = COUNTRIES;
  const timezoneOptions = useMemo(() => getTimezoneOptions(), []);
  const currencyOptions = useMemo(() => {
    const options = Object.keys(tiktokCurrenciesData).map((currencyCode) => ({
      value: currencyCode,
      label: `${currencyCode} - ${tiktokCurrenciesData[currencyCode].currency}`,
    }));
    if (lockedCurrency && !tiktokCurrenciesData[lockedCurrency]) {
      options.push({ value: lockedCurrency, label: lockedCurrency });
    }
    return options;
  }, [lockedCurrency]);

  const connectAccountOption = useMemo(
    () => ({
      value: CONNECT_ACCOUNT_OPTION_VALUE,
      label: t(BUTTON_CONNECT_ACCOUNT),
      isActionOption: true,
    }),
    [t]
  );

  const businessCenterOptions = useMemo(
    () => [
      connectAccountOption,
      ...businessCenters.map((businessCenter) => ({
        value: buildBusinessCenterValue({
          integrationID: businessCenter.integrationID,
          bcID: businessCenter.id,
        }),
        label: businessCenter.name,
      })),
    ],
    [businessCenters, connectAccountOption]
  );

  const getInitialData = async () => {
    const generation = loadGenerationRef.current + 1;
    loadGenerationRef.current = generation;
    setIsLoading(true);

    const [brandResponse, businessCentersResponses] = await Promise.all([
      dispatch(startGetBrandInformation()),
      Promise.all(
        integrationIDs.map(async (integrationID) => {
          const response = await dispatch(
            startGetBusinessCentersTikTokBusiness({ integrationID })
          );
          return { integrationID, response };
        })
      ),
    ]);

    // A connection can change the integration list while this modal stays open.
    // Drop the slower response so it cannot clear the centers just loaded.
    if (generation !== loadGenerationRef.current) return;

    if (brandResponse?.ok) {
      const businessName = brandResponse.data?.businessName;
      if (businessName) {
        setValue("name", businessName.slice(0, MAX_NAME_LENGTH));
        setValue("company", businessName.slice(0, MAX_COMPANY_LENGTH));
      }
    }

    const uniqueBusinessCenters = new Map();
    businessCentersResponses.forEach(({ integrationID, response }) => {
      if (!response?.ok) return;
      response.data.forEach((businessCenter) => {
        if (uniqueBusinessCenters.has(businessCenter.id)) return;
        uniqueBusinessCenters.set(businessCenter.id, {
          ...businessCenter,
          integrationID,
        });
      });
    });

    const nextBusinessCenters = Array.from(uniqueBusinessCenters.values());
    setBusinessCenters(nextBusinessCenters);

    if (nextBusinessCenters.length === 1) {
      const [onlyBusinessCenter] = nextBusinessCenters;
      setValue(
        "businessCenter",
        buildBusinessCenterValue({
          integrationID: onlyBusinessCenter.integrationID,
          bcID: onlyBusinessCenter.id,
        })
      );
      applyBusinessCenterDefaults(onlyBusinessCenter);
    }

    setIsLoading(false);
  };

  const applyBusinessCenterDefaults = (businessCenter) => {
    const businessCenterCurrency = businessCenter?.currency || "";
    setLockedCurrency(businessCenterCurrency);
    if (businessCenterCurrency) {
      setValue("currency", businessCenterCurrency);
    }
    if (businessCenter?.registeredArea) {
      setValue("country", businessCenter.registeredArea);
    }
    if (businessCenter?.timezone) {
      setValue("timezone", businessCenter.timezone);
    }
  };

  const onChangeBusinessCenter = (value) => {
    const selectedBusinessCenter = businessCenters.find(
      (businessCenter) =>
        buildBusinessCenterValue({
          integrationID: businessCenter.integrationID,
          bcID: businessCenter.id,
        }) === value
    );
    applyBusinessCenterDefaults(selectedBusinessCenter);
  };

  const onActionOptionSelect = (option) => {
    if (option?.value !== CONNECT_ACCOUNT_OPTION_VALUE) return;
    onConnectAccount();
  };

  const onSubmit = async (data, e) => {
    e?.preventDefault();
    e?.stopPropagation();

    const { integrationID, bcID } = parseBusinessCenterValue(
      data.businessCenter
    );

    setIsFetching(true);
    const response = await dispatch(
      startCreateAdvertiserTikTokBusiness({
        integrationID,
        bcID,
        name: data.name.trim(),
        company: data.company.trim(),
        industry: data.industry,
        registeredArea: data.country,
        currency: data.currency,
        timezone: data.timezone,
      })
    );
    setIsFetching(false);

    if (!response?.ok) {
      return;
    }

    onCloseModal();
    await SimpleAlert({
      title: t(MODAL_CREATE_AD_ACCOUNT_TIKTOK_SUCCESS_TITLE),
      text: t(MODAL_CREATE_AD_ACCOUNT_TIKTOK_REVIEW_NOTE, { NAME_BUSINESS }),
      icon: ALERT_ICON_TYPE_SUCCESS,
      confirmButtonText: t(OK),
    });
    onCallbackCreate(response.data, integrationID);
  };

  const integrationIDsKey = integrationIDs.join(",");

  useEffect(() => {
    if (!modalOpen) return;
    getInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, integrationIDsKey]);

  const hasBusinessCenters = businessCenters.length > 0;

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      maxWidth="sm"
      scroll="paper"
      title={t(MODAL_CREATE_AD_ACCOUNT_TIKTOK_TITLE)}
      description={t(MODAL_CREATE_AD_ACCOUNT_TIKTOK_DESCRIPTION)}
    >
      {isLoading && <Loader />}
      {!isLoading && (
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
        >
          <Alert
            severity="success"
            variant="outlined"
            sx={{
              backgroundColor: "transparent",
              color: "text.primary",
              borderColor: "success.main",
              "& .MuiAlert-icon": { color: "success.main" },
            }}
          >
            {t(MODAL_CREATE_AD_ACCOUNT_TIKTOK_CREDIT_ALERT, { NAME_BUSINESS })}
          </Alert>
          <AutocompleteField
            control={control}
            errors={errors}
            name="businessCenter"
            label={t(MODAL_CREATE_AD_ACCOUNT_TIKTOK_BUSINESS_CENTER)}
            options={businessCenterOptions}
            rules={requiredRule}
            disableClearable
            onActionOptionSelect={onActionOptionSelect}
            onValueChange={onChangeBusinessCenter}
            filterOptions={(options, params) => {
              const filtered = filter(
                options.filter((option) => !option.isActionOption),
                params
              );
              return [connectAccountOption, ...filtered];
            }}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              return (
                <Box
                  component="li"
                  key={key}
                  {...optionProps}
                  sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                >
                  {option.isActionOption && <AddIcon fontSize="small" />}
                  {option.label}
                </Box>
              );
            }}
          />
          <TextField
            name="name"
            label={t(MODAL_CREATE_AD_ACCOUNT_TIKTOK_NAME)}
            variant="filled"
            fullWidth
            control={control}
            errors={errors}
            rules={{
              ...requiredRule,
              maxLength: {
                value: MAX_NAME_LENGTH,
                message: t(FIELD_MAX_LENGTH, { value: MAX_NAME_LENGTH }),
              },
            }}
          />
          <TextField
            name="company"
            label={t(MODAL_CREATE_AD_ACCOUNT_TIKTOK_COMPANY)}
            variant="filled"
            fullWidth
            control={control}
            errors={errors}
            rules={{
              ...requiredRule,
              maxLength: {
                value: MAX_COMPANY_LENGTH,
                message: t(FIELD_MAX_LENGTH, { value: MAX_COMPANY_LENGTH }),
              },
            }}
          />
          <AutocompleteField
            control={control}
            errors={errors}
            name="industry"
            label={t(MODAL_CREATE_AD_ACCOUNT_TIKTOK_INDUSTRY)}
            options={industryOptions}
            rules={requiredRule}
            doTranslate
            disableClearable
          />
          <AutocompleteField
            control={control}
            errors={errors}
            name="country"
            label={t(MODAL_CREATE_AD_ACCOUNT_TIKTOK_COUNTRY)}
            options={countryOptions}
            rules={requiredRule}
            doTranslate
            disableClearable
          />
          <AutocompleteField
            control={control}
            errors={errors}
            name="currency"
            label={t(MODAL_CREATE_AD_ACCOUNT_TIKTOK_CURRENCY)}
            options={currencyOptions}
            rules={requiredRule}
            disableClearable
            disabled={Boolean(lockedCurrency)}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
            {t(MODAL_CREATE_AD_ACCOUNT_TIKTOK_CURRENCY_HELPER)}
          </Typography>
          <AutocompleteField
            control={control}
            errors={errors}
            name="timezone"
            label={t(MODAL_CREATE_AD_ACCOUNT_TIKTOK_TIMEZONE)}
            options={timezoneOptions}
            rules={requiredRule}
            disableClearable
          />
          <Typography variant="caption" color="text.secondary">
            {t(MODAL_CREATE_AD_ACCOUNT_TIKTOK_REVIEW_NOTE, { NAME_BUSINESS })}
          </Typography>
          <ContainerModalActions
            sx={{ mt: 1 }}
            hasPadding={false}
            propsSecondaryButton={{
              showButton: true,
              onClick: onCloseModal,
              disabled: isFetching,
              text: BUTTON_GO_BACK,
            }}
            propsPrimaryButton={{
              showButton: true,
              onClick: handleSubmit(onSubmit),
              loading: isFetching,
              disabled: !hasBusinessCenters,
              text: BUTTON_CREATE,
              type: "button",
            }}
          />
        </Box>
      )}
    </ModalBasicLayout>
  );
};

export default ModalCreateAdAccountTikTokBusiness;
